import React from "react"
import cx from "classnames"
import Icon from "./icons"
import {Link} from "react-router-dom"

/*  Common buttons. Default is a bordered button.
 *  Different variants and sizes and supported as well.
 */

const ButtonInner = ({className, iconPosition = "left", icon, iconSize, children}) => (
  <div className={cx(className, "button-inner", iconPosition === "right" && "flex-row-reverse")}>
    {icon && <Icon name={icon} size={iconSize} />}
    {children}
  </div>
)

export const Button = ({variant = "outline", size = "medium", className, href, to, onClick, icon, type, iconPosition, iconSize, children, ...props}) => {
  const variantClasses = {
    primary: "text-on-primary bg-primary-1",
    secondary: "text-on-secondary bg-secondary-1",
    tertiary: "text-neutral-2 bg-tertiary-1",
    dark: "text-neutral-20 bg-neutral-2 hover:bg-neutral-9",
    danger: "text-neutral-2 border-2 border-danger-1 hover:bg-darken py-2.5 px-[1.375rem] disabled:bg-neutral-19",
    outline: "text-neutral-2 border-2 border-neutral-17 hover:bg-darken py-2.5 px-[1.375rem] disabled:bg-neutral-19",
    text: "border-none hover:bg-darken disabled:shadow-disabled"
  }

  const buttonClasses = cx("common-button", variantClasses[variant], className)

  const Tag = to ? Link : href ? "a" : "button"
  const getType = !type && Tag === "button" ? "button" : type

  return (
    <Tag to={to} href={href} onClick={onClick} className={cx(className, buttonClasses)} type={getType} {...props}>
      <ButtonInner icon={icon} iconPosition={iconPosition} iconSize={iconSize}>
        {children}
      </ButtonInner>
    </Tag>
  )
}

export const PrimaryButton = ({children, ...props}) => (
  <Button variant="primary" {...props}>
    {children}
  </Button>
)

export const SecondaryButton = ({className, children, ...props}) => (
  <Button variant="secondary" {...props}>
    {children}
  </Button>
)

export const TertiaryButton = ({className, children, ...props}) => (
  <Button variant="tertiary" {...props}>
    {children}
  </Button>
)

export const DangerButton = ({className, children, ...props}) => (
  <Button variant="danger" {...props}>
    {children}
  </Button>
)

export const TextButton = ({className, children, ...props}) => (
  <Button className={cx(className, "border-none text-primary-1")} {...props}>
    {children}
  </Button>
)

export const TextButton2 = ({className, children, ...props}) => (
  <Button className={cx(className)} variant="text" {...props}>
    {children}
  </Button>
)

const UnderlineText = ({children}) => <span className="link-underline font-vs-semibold text-neutral-2 decoration-primary-1">{children}</span>

export const UnderlineTextButton = ({className, to, href, icon, iconPosition, onClick, children, ...props}) => {
  const Tag = to ? Link : href ? "a" : "button"

  return (
    <Tag to={to} href={href} onClick={onClick} className={cx(className, "group/ubtn")} {...props}>
      <ButtonInner icon={icon} iconPosition={iconPosition} className="underline-button-inner">
        <UnderlineText>{children}</UnderlineText>
      </ButtonInner>
    </Tag>
  )
}

export const PrimaryOutlineButton = ({className, children, ...props}) => (
  <Button variant="outline" className="bg-primary-1 border-neutral-20 text-on-primary hover:shadow-lighten" {...props}>
    {children}
  </Button>
)

export const IconButton = ({variant = "filled", className, label, icon, to, href, onClick, ...props}) => {
  const variantClasses = {
    filled: "p-3",
    outlined: "border-2 border-neutral-17 p-2.5",
    small: "h-9 w-9 flex items-center justify-center"
  }

  const Tag = to ? Link : href ? "a" : "button"

  return (
    <Tag aria-label={label} to={to} href={href} onClick={onClick} className={cx("icon-button", variantClasses[variant], className)} {...props}>
      <Icon name={icon} />
    </Tag>
  )
}
